import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BudgetCard from "@/components/BudgetCard";
import StatusBadge from "@/components/StatusBadge";
import { useCart } from "@/contexts/CartContext";
import { Building2, ShoppingCart, FileText, Wallet, MessageSquareWarning, ArrowRight, Sparkles, Sparkle } from "lucide-react";

export default function EmployeeDashboard() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotedCount, setQuotedCount] = useState(0);
  const { total: cartTotal, count: cartCount } = useCart();

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/orders").then((r) => setOrders(r.data.slice(0, 5)));
    api.get("/custom-product-requests/quoted").then((r) => setQuotedCount((r.data || []).length)).catch(() => {});
  }, []);

  if (!summary) return <div className="text-sm text-slate-500">Ładowanie…</div>;
  if (!summary.site) {
    return (
      <div className="ol-card p-6 text-sm">
        <p className="text-slate-700">Twoje konto nie ma jeszcze przypisanej budowy. Skontaktuj się z Office-Land.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        testId="employee-dashboard"
        title="Dashboard budowy"
        subtitle={summary.site.name}
        actions={
          <>
            <Link
              to="/catalog"
              data-testid="cta-go-catalog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors"
            >
              <ShoppingCart className="w-4 h-4" /> Złóż zamówienie
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <BudgetCard
            monthly={summary.monthly_budget}
            used={summary.used_budget_current_month}
            remaining={summary.remaining}
            pendingValue={cartTotal}
          />
        </div>

        <div className="space-y-4">
          <div className="ol-kpi">
            <div className="flex items-center gap-2 mb-2 text-slate-500">
              <Building2 className="w-4 h-4" />
              <p className="text-xs font-semibold uppercase tracking-wide">Budowa</p>
            </div>
            <p className="text-lg font-bold text-slate-900 leading-tight">{summary.site.name}</p>
            <p className="text-xs text-slate-500 mt-1">NDI · {summary.site.approval_required_for_all_orders ? "Każde zamówienie wymaga akceptacji" : `Auto-akceptacja do ${formatPLN(summary.site.auto_approve_limit)}`}</p>
          </div>
          <div className="ol-kpi">
            <div className="flex items-center gap-2 mb-2 text-slate-500">
              <ShoppingCart className="w-4 h-4" />
              <p className="text-xs font-semibold uppercase tracking-wide">Aktualne zamówienie</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatPLN(cartTotal)}</p>
            <p className="text-xs text-slate-500 mt-1">{cartCount} pozycji w koszyku</p>
            <Link to="/cart" data-testid="cart-link" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#FF6B1A] hover:text-[#E65A10]">
              Przejdź do koszyka <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>

      {quotedCount > 0 && (
        <Link
          to="/custom-request"
          data-testid="quoted-banner"
          className="block ol-card p-4 mb-4 border-2 border-[#FF6B1A]/40 bg-gradient-to-r from-[#FFF4ED] via-[#FFF4ED] to-white hover:border-[#FF6B1A] transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#FF6B1A] flex items-center justify-center flex-shrink-0">
              <Sparkle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">
                Masz {quotedCount} {quotedCount === 1 ? "nową wycenę" : "nowe wyceny"} Office-Land
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                Produkty spoza katalogu z wyceną — możesz je dodać do zamówienia jednym kliknięciem.
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#CC4B00] flex-shrink-0" />
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Link to="/catalog" className="ol-card p-5 hover:border-[#FF6B1A] transition-colors group" data-testid="quick-catalog">
          <ShoppingCart className="w-5 h-5 text-[#FF6B1A] mb-2" />
          <p className="text-sm font-semibold text-slate-900">Katalog produktów</p>
          <p className="text-xs text-slate-500 mt-1">Przeglądaj i zamawiaj z katalogu NDI.</p>
        </Link>
        <Link to="/suggestions" className="ol-card p-5 hover:border-[#FF6B1A] transition-colors" data-testid="quick-suggestions">
          <Sparkles className="w-5 h-5 text-[#FF6B1A] mb-2" />
          <p className="text-sm font-semibold text-slate-900">Sugestie zamówień</p>
          <p className="text-xs text-slate-500 mt-1">Najczęściej zamawiane, powtórz ostatnie, propozycje z historii.</p>
        </Link>
        <Link to="/custom-request" className="ol-card p-5 hover:border-[#FF6B1A] transition-colors" data-testid="quick-custom">
          <MessageSquareWarning className="w-5 h-5 text-[#FF6B1A] mb-2" />
          <p className="text-sm font-semibold text-slate-900">Produkt spoza katalogu</p>
          <p className="text-xs text-slate-500 mt-1">Brakuje czegoś? Wyślij zapytanie.</p>
        </Link>
        <Link to="/budget-request" className="ol-card p-5 hover:border-[#FF6B1A] transition-colors" data-testid="quick-budget">
          <Wallet className="w-5 h-5 text-[#FF6B1A] mb-2" />
          <p className="text-sm font-semibold text-slate-900">Zwiększenie budżetu</p>
          <p className="text-xs text-slate-500 mt-1">Złóż wniosek o zwiększenie limitu.</p>
        </Link>
      </div>

      <div className="ol-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Ostatnie zamówienia
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Najnowsze 5 zamówień</p>
          </div>
          <Link to="/my-orders" data-testid="see-all-orders" className="text-xs font-semibold text-[#FF6B1A] hover:text-[#E65A10]">
            Zobacz wszystkie
          </Link>
        </div>
        {orders.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Brak zamówień. <Link to="/catalog" className="ol-link">Złóż pierwsze zamówienie</Link>.</div>
        ) : (
          <table className="ol-table" data-testid="recent-orders-table">
            <thead>
              <tr>
                <th>Numer</th>
                <th>Data</th>
                <th>Pozycje</th>
                <th>Wartość</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="font-mono text-xs text-slate-700">{o.order_number}</td>
                  <td className="text-slate-600">{new Date(o.created_at).toLocaleDateString("pl-PL")}</td>
                  <td>{o.items?.length || 0}</td>
                  <td className="font-semibold">{formatPLN(o.final_total_net)}</td>
                  <td><StatusBadge status={o.status} /></td>
                  <td>
                    <Link to={`/orders/${o.id}`} className="ol-link text-xs" data-testid={`view-order-${o.order_number}`}>Szczegóły</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
